/**
 * ! Do not edit manually
 * * The file has been auto-generated using Orval for SigNoz
 * * regenerate with 'pnpm generate:api'
 * SigNoz
 */
import { useMutation, useQuery } from 'react-query';
import type {
	InvalidateOptions,
	MutationFunction,
	QueryClient,
	QueryFunction,
	QueryKey,
	UseMutationOptions,
	UseMutationResult,
	UseQueryOptions,
	UseQueryResult,
} from 'react-query';

import type {
	CreateSavedView201,
	DeleteSavedViewPathParameters,
	GetSavedView200,
	GetSavedViewPathParameters,
	ListSavedViews200,
	ListSavedViewsParams,
	RenderErrorResponseDTO,
	SavedviewtypesPostableSavedViewDTO,
	SavedviewtypesUpdatableSavedViewDTO,
	UpdateSavedViewPathParameters,
} from '../sigNoz.schemas';

import { GeneratedAPIInstance } from '../../../generatedAPIInstance';
import type { ErrorType, BodyType } from '../../../generatedAPIInstance';

const withQueryKey = <T extends object, K>(
	query: T,
	queryKey: K,
): T & { queryKey: K } => {
	const result = { queryKey } as T & { queryKey: K };
	for (const key of Object.keys(query)) {
		// The explicit queryKey always wins, matching the previous
		// `{ ...query, queryKey }` spread where it was set last.
		if (key === 'queryKey') {
			continue;
		}
		Object.defineProperty(result, key, {
			enumerable: true,
			configurable: true,
			get: () => (query as Record<string, unknown>)[key],
		});
	}
	return result;
};

/**
 * Returns saved views, optionally filtered by source and name.
 * @summary List saved views
 */
export const listSavedViews = (
	params?: ListSavedViewsParams,
	signal?: AbortSignal,
) => {
	return GeneratedAPIInstance<ListSavedViews200>({
		url: `/api/v2/saved_views`,
		method: 'GET',
		params,
		signal,
	});
};

export const getListSavedViewsQueryKey = (params?: ListSavedViewsParams) => {
	return [`/api/v2/saved_views`, ...(params ? [params] : [])] as const;
};

export const getListSavedViewsQueryOptions = <
	TData = Awaited<ReturnType<typeof listSavedViews>>,
	TError = ErrorType<RenderErrorResponseDTO>,
>(
	params?: ListSavedViewsParams,
	options?: {
		query?: UseQueryOptions<
			Awaited<ReturnType<typeof listSavedViews>>,
			TError,
			TData
		>;
	},
) => {
	const { query: queryOptions } = options ?? {};

	const queryKey = queryOptions?.queryKey ?? getListSavedViewsQueryKey(params);

	const queryFn: QueryFunction<Awaited<ReturnType<typeof listSavedViews>>> = ({
		signal,
	}) => listSavedViews(params, signal);

	return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
		Awaited<ReturnType<typeof listSavedViews>>,
		TError,
		TData
	> & { queryKey: QueryKey };
};

export type ListSavedViewsQueryResult = NonNullable<
	Awaited<ReturnType<typeof listSavedViews>>
>;
export type ListSavedViewsQueryError = ErrorType<RenderErrorResponseDTO>;

/**
 * @summary List saved views
 */

export function useListSavedViews<
	TData = Awaited<ReturnType<typeof listSavedViews>>,
	TError = ErrorType<RenderErrorResponseDTO>,
>(
	params?: ListSavedViewsParams,
	options?: {
		query?: UseQueryOptions<
			Awaited<ReturnType<typeof listSavedViews>>,
			TError,
			TData
		>;
	},
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
	const queryOptions = getListSavedViewsQueryOptions(params, options);

	const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
		queryKey: QueryKey;
	};

	return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary List saved views
 */
export const invalidateListSavedViews = async (
	queryClient: QueryClient,
	params?: ListSavedViewsParams,
	options?: InvalidateOptions,
): Promise<QueryClient> => {
	await queryClient.invalidateQueries(
		{ queryKey: getListSavedViewsQueryKey(params) },
		options,
	);

	return queryClient;
};

/**
 * Persists a saved view for the explore page. Returns the id of the created view.
 * @summary Create saved view
 */
export const createSavedView = (
	savedviewtypesPostableSavedViewDTO?: BodyType<SavedviewtypesPostableSavedViewDTO>,
	signal?: AbortSignal,
) => {
	return GeneratedAPIInstance<CreateSavedView201>({
		url: `/api/v2/saved_views`,
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		data: savedviewtypesPostableSavedViewDTO,
		signal,
	});
};

export const getCreateSavedViewMutationOptions = <
	TError = ErrorType<RenderErrorResponseDTO>,
	TContext = unknown,
>(options?: {
	mutation?: UseMutationOptions<
		Awaited<ReturnType<typeof createSavedView>>,
		TError,
		{ data?: BodyType<SavedviewtypesPostableSavedViewDTO> },
		TContext
	>;
}): UseMutationOptions<
	Awaited<ReturnType<typeof createSavedView>>,
	TError,
	{ data?: BodyType<SavedviewtypesPostableSavedViewDTO> },
	TContext
> => {
	const mutationKey = ['createSavedView'];
	const { mutation: mutationOptions } = options
		? options.mutation &&
			'mutationKey' in options.mutation &&
			options.mutation.mutationKey
			? options
			: { ...options, mutation: { ...options.mutation, mutationKey } }
		: { mutation: { mutationKey } };

	const mutationFn: MutationFunction<
		Awaited<ReturnType<typeof createSavedView>>,
		{ data?: BodyType<SavedviewtypesPostableSavedViewDTO> }
	> = (props) => {
		const { data } = props ?? {};

		return createSavedView(data);
	};

	return { mutationFn, ...mutationOptions };
};

export type CreateSavedViewMutationResult = NonNullable<
	Awaited<ReturnType<typeof createSavedView>>
>;
export type CreateSavedViewMutationBody =
	| BodyType<SavedviewtypesPostableSavedViewDTO>
	| undefined;
export type CreateSavedViewMutationError = ErrorType<RenderErrorResponseDTO>;

/**
 * @summary Create saved view
 */
export const useCreateSavedView = <
	TError = ErrorType<RenderErrorResponseDTO>,
	TContext = unknown,
>(options?: {
	mutation?: UseMutationOptions<
		Awaited<ReturnType<typeof createSavedView>>,
		TError,
		{ data?: BodyType<SavedviewtypesPostableSavedViewDTO> },
		TContext
	>;
}): UseMutationResult<
	Awaited<ReturnType<typeof createSavedView>>,
	TError,
	{ data?: BodyType<SavedviewtypesPostableSavedViewDTO> },
	TContext
> => {
	return useMutation(getCreateSavedViewMutationOptions(options));
};
/**
 * Deletes a saved view by id.
 * @summary Delete saved view
 */
export const deleteSavedView = (
	{ id }: DeleteSavedViewPathParameters,
	signal?: AbortSignal,
) => {
	return GeneratedAPIInstance<void>({
		url: `/api/v2/saved_views/${id}`,
		method: 'DELETE',
		signal,
	});
};

export const getDeleteSavedViewMutationOptions = <
	TError = ErrorType<RenderErrorResponseDTO>,
	TContext = unknown,
>(options?: {
	mutation?: UseMutationOptions<
		Awaited<ReturnType<typeof deleteSavedView>>,
		TError,
		{ pathParams: DeleteSavedViewPathParameters },
		TContext
	>;
}): UseMutationOptions<
	Awaited<ReturnType<typeof deleteSavedView>>,
	TError,
	{ pathParams: DeleteSavedViewPathParameters },
	TContext
> => {
	const mutationKey = ['deleteSavedView'];
	const { mutation: mutationOptions } = options
		? options.mutation &&
			'mutationKey' in options.mutation &&
			options.mutation.mutationKey
			? options
			: { ...options, mutation: { ...options.mutation, mutationKey } }
		: { mutation: { mutationKey } };

	const mutationFn: MutationFunction<
		Awaited<ReturnType<typeof deleteSavedView>>,
		{ pathParams: DeleteSavedViewPathParameters }
	> = (props) => {
		const { pathParams } = props ?? {};

		return deleteSavedView(pathParams);
	};

	return { mutationFn, ...mutationOptions };
};

export type DeleteSavedViewMutationResult = NonNullable<
	Awaited<ReturnType<typeof deleteSavedView>>
>;

export type DeleteSavedViewMutationError = ErrorType<RenderErrorResponseDTO>;

/**
 * @summary Delete saved view
 */
export const useDeleteSavedView = <
	TError = ErrorType<RenderErrorResponseDTO>,
	TContext = unknown,
>(options?: {
	mutation?: UseMutationOptions<
		Awaited<ReturnType<typeof deleteSavedView>>,
		TError,
		{ pathParams: DeleteSavedViewPathParameters },
		TContext
	>;
}): UseMutationResult<
	Awaited<ReturnType<typeof deleteSavedView>>,
	TError,
	{ pathParams: DeleteSavedViewPathParameters },
	TContext
> => {
	return useMutation(getDeleteSavedViewMutationOptions(options));
};
/**
 * Returns a saved view by id.
 * @summary Get saved view
 */
export const getSavedView = (
	{ id }: GetSavedViewPathParameters,
	signal?: AbortSignal,
) => {
	return GeneratedAPIInstance<GetSavedView200>({
		url: `/api/v2/saved_views/${id}`,
		method: 'GET',
		signal,
	});
};

export const getGetSavedViewQueryKey = ({ id }: GetSavedViewPathParameters) => {
	return [`/api/v2/saved_views/${id}`] as const;
};

export const getGetSavedViewQueryOptions = <
	TData = Awaited<ReturnType<typeof getSavedView>>,
	TError = ErrorType<RenderErrorResponseDTO>,
>(
	{ id }: GetSavedViewPathParameters,
	options?: {
		query?: UseQueryOptions<
			Awaited<ReturnType<typeof getSavedView>>,
			TError,
			TData
		>;
	},
) => {
	const { query: queryOptions } = options ?? {};

	const queryKey = queryOptions?.queryKey ?? getGetSavedViewQueryKey({ id });

	const queryFn: QueryFunction<Awaited<ReturnType<typeof getSavedView>>> = ({
		signal,
	}) => getSavedView({ id }, signal);

	return {
		queryKey,
		queryFn,
		enabled: id !== null && id !== undefined,
		...queryOptions,
	} as UseQueryOptions<
		Awaited<ReturnType<typeof getSavedView>>,
		TError,
		TData
	> & { queryKey: QueryKey };
};

export type GetSavedViewQueryResult = NonNullable<
	Awaited<ReturnType<typeof getSavedView>>
>;
export type GetSavedViewQueryError = ErrorType<RenderErrorResponseDTO>;

/**
 * @summary Get saved view
 */

export function useGetSavedView<
	TData = Awaited<ReturnType<typeof getSavedView>>,
	TError = ErrorType<RenderErrorResponseDTO>,
>(
	{ id }: GetSavedViewPathParameters,
	options?: {
		query?: UseQueryOptions<
			Awaited<ReturnType<typeof getSavedView>>,
			TError,
			TData
		>;
	},
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
	const queryOptions = getGetSavedViewQueryOptions({ id }, options);

	const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
		queryKey: QueryKey;
	};

	return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get saved view
 */
export const invalidateGetSavedView = async (
	queryClient: QueryClient,
	{ id }: GetSavedViewPathParameters,
	options?: InvalidateOptions,
): Promise<QueryClient> => {
	await queryClient.invalidateQueries(
		{ queryKey: getGetSavedViewQueryKey({ id }) },
		options,
	);

	return queryClient;
};

/**
 * Replaces a saved view's name and query.
 * @summary Update saved view
 */
export const updateSavedView = (
	{ id }: UpdateSavedViewPathParameters,
	savedviewtypesUpdatableSavedViewDTO?: BodyType<SavedviewtypesUpdatableSavedViewDTO>,
	signal?: AbortSignal,
) => {
	return GeneratedAPIInstance<void>({
		url: `/api/v2/saved_views/${id}`,
		method: 'PUT',
		headers: { 'Content-Type': 'application/json' },
		data: savedviewtypesUpdatableSavedViewDTO,
		signal,
	});
};

export const getUpdateSavedViewMutationOptions = <
	TError = ErrorType<RenderErrorResponseDTO>,
	TContext = unknown,
>(options?: {
	mutation?: UseMutationOptions<
		Awaited<ReturnType<typeof updateSavedView>>,
		TError,
		{
			pathParams: UpdateSavedViewPathParameters;
			data?: BodyType<SavedviewtypesUpdatableSavedViewDTO>;
		},
		TContext
	>;
}): UseMutationOptions<
	Awaited<ReturnType<typeof updateSavedView>>,
	TError,
	{
		pathParams: UpdateSavedViewPathParameters;
		data?: BodyType<SavedviewtypesUpdatableSavedViewDTO>;
	},
	TContext
> => {
	const mutationKey = ['updateSavedView'];
	const { mutation: mutationOptions } = options
		? options.mutation &&
			'mutationKey' in options.mutation &&
			options.mutation.mutationKey
			? options
			: { ...options, mutation: { ...options.mutation, mutationKey } }
		: { mutation: { mutationKey } };

	const mutationFn: MutationFunction<
		Awaited<ReturnType<typeof updateSavedView>>,
		{
			pathParams: UpdateSavedViewPathParameters;
			data?: BodyType<SavedviewtypesUpdatableSavedViewDTO>;
		}
	> = (props) => {
		const { pathParams, data } = props ?? {};

		return updateSavedView(pathParams, data);
	};

	return { mutationFn, ...mutationOptions };
};

export type UpdateSavedViewMutationResult = NonNullable<
	Awaited<ReturnType<typeof updateSavedView>>
>;
export type UpdateSavedViewMutationBody =
	| BodyType<SavedviewtypesUpdatableSavedViewDTO>
	| undefined;
export type UpdateSavedViewMutationError = ErrorType<RenderErrorResponseDTO>;

/**
 * @summary Update saved view
 */
export const useUpdateSavedView = <
	TError = ErrorType<RenderErrorResponseDTO>,
	TContext = unknown,
>(options?: {
	mutation?: UseMutationOptions<
		Awaited<ReturnType<typeof updateSavedView>>,
		TError,
		{
			pathParams: UpdateSavedViewPathParameters;
			data?: BodyType<SavedviewtypesUpdatableSavedViewDTO>;
		},
		TContext
	>;
}): UseMutationResult<
	Awaited<ReturnType<typeof updateSavedView>>,
	TError,
	{
		pathParams: UpdateSavedViewPathParameters;
		data?: BodyType<SavedviewtypesUpdatableSavedViewDTO>;
	},
	TContext
> => {
	return useMutation(getUpdateSavedViewMutationOptions(options));
};
